import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Hub } from '../hubs/hub.model';
import { HubService } from '../hubs/hub.service';
import { BreadcrumbService } from '../navigation/breadcrumb/breadcrumb.service';

/**
 * Which hub an Ideas screen belongs to, if any: the `:hub` parameter of an
 * ancestor route. The same screens serve the public area at `/ideas` and a
 * hub's own view at `/hubs/:hub/ideas`, where the list shows that hub's
 * requests and a submission is private to it.
 */
export function ideasHubSlug(route: ActivatedRoute): string | null {
  for (const snapshot of route.snapshot.pathFromRoot) {
    const hub = snapshot.paramMap.get('hub');

    if (hub) {
      return hub;
    }
  }

  return null;
}

/**
 * Name the hub in its breadcrumb fragment, as the other hub pages do — the
 * `:hub` route's label comes from whichever page sets it. Call it in a
 * constructor: it asks for the hub through `HubService.getManagedHub`
 * (cached, and shared while in flight). The returned function is a fallback
 * for a viewer that request refuses (a hub owner or contact person): pass it
 * the hub name the Ideas API sent.
 *
 * The breadcrumb clears every fragment on `NavigationEnd`, so a name set while
 * the navigation is still running — from a cache, say — would be wiped; both
 * routes wait for the navigation to settle first.
 */
export function nameHubInBreadcrumb(hub: string | null): (fallbackName: string | null | undefined) => void {
  const breadcrumbs = inject(BreadcrumbService);
  const hubService = inject(HubService);
  const router = inject(Router);
  const destroyRef = inject(DestroyRef);
  let named = false;

  const setName = (name: string | null | undefined) => {
    if (!name) {
      return;
    }
    named = true;
    breadcrumbs.setBreadcrumbFragment({ urlFragment: 'hub', fragmentName: name });
  };
  const settled$ = (): Observable<unknown> =>
    router.getCurrentNavigation()
      ? router.events.pipe(
          filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
          take(1),
        )
      : of(null);

  if (hub) {
    settled$()
      .pipe(
        switchMap(() => hubService.getManagedHub(hub)),
        catchError(() => EMPTY),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe((managed: Hub) => setName(managed?.name));
  }

  return (fallbackName) => {
    if (named || !hub) {
      return;
    }
    settled$()
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(() => {
        if (!named) {
          setName(fallbackName);
        }
      });
  };
}

/** Where the list lives for this screen: `['/ideas']` or `['/hubs', hub, 'ideas']`. */
export function ideasBaseRoute(hub: string | null): string[] {
  return hub ? ['/hubs', hub, 'ideas'] : ['/ideas'];
}
